import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useActionData, useFetcher } from 'react-router-dom';
import { uploadFileFromObject } from '../../api/common';
import { Modal } from '../../components/ui';
import { FilePreview } from '../../components/ui/file-preview.jsx';
import SingleFileUpload from '../../components/ui/multifile-upload.jsx';
import i18n from '../../i18n.js';
import { getPublicFileUrl } from '../../library/constants';
import styles from './project-item-address.module.scss';

export const BulkDeliveryAddresses = ({ isConfirmed, uploadedFile, quotationToken }) => {
    const { t } = useTranslation()
    const { errors = {} } = useActionData() || {}
    const [addressPreview, setAddressPreview] = useState(false)
    const fileUploaderRef = useRef([])
    const [isLoading, setIsLoading] = useState(false)
    const fetcher = useFetcher()

    useEffect(() => {
        setIsLoading(fetcher.state !== 'idle')
    }, [fetcher.state])

    const handleFilesUpload = async () => {
        const hasNewFile = fileUploaderRef.current.length > 0;
        const hasExistingFile = !!uploadedFile;

        if (!hasNewFile && !hasExistingFile) {
            alert(i18n.t('Please select at least one file'));
            return;
        }

        setIsLoading(true);
        let fileId = null;

        try {
            if (hasNewFile) {
                // Upload newly selected file(s)
                const promises = fileUploaderRef.current.map(file =>
                    uploadFileFromObject(quotationToken, file)
                );
                const fileIds = await Promise.all(promises);
                fileId = fileIds.pop(); // Use the last uploaded file ID
            } else if (hasExistingFile) {
                // Use the existing uploaded file's identifier
                fileId = uploadedFile.id || uploadedFile.fileId || null;
            }

            // Submit file ID to backend
            fetcher.submit(
                {
                    fileId,
                    action: 'bulkDeliveryAddress',
                },
                {
                    method: 'post',
                    encType: 'application/json',
                }
            );
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            return new Response('Unable to upload files', { status: 200 });
        }

        setIsLoading(false);
    };


    if (isConfirmed) {
        return (
            <div>
                <p>{t("Below is the uploaded addresses file confirmed by you")}</p>
                <FilePreview name={uploadedFile?.name} comment={uploadedFile?.notes} commentLabel={t("Notes")} path={uploadedFile?.url} quotationToken={quotationToken} />
            </div>
        )
    }

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} encType="multipart/form-data">
                {!!errors.addressFile && errors.addressFile.map((error, index) => (
                    <p key={index} className='error'>{error}</p>
                ))}

                {/* Show existing uploaded file preview if it exists */}
                {uploadedFile && (
                    <div className={`box--light-gray ${styles.addressSelector}`}>
                        <p>{t("You have already uploaded an addresses file. Please review it below.")}</p>
                        <FilePreview
                            name={uploadedFile?.name}
                            comment={uploadedFile?.notes}
                            commentLabel={t("Notes")}
                            path={uploadedFile?.url}
                            quotationToken={quotationToken}
                        />
                    </div>
                )}

                {/* Show uploader and sample download regardless */}
                <div className={`box--light-gray ${styles.addressSelector}`}>
                    <p>{t("You can upload a new address file if needed.")}</p>
                    <Link
                        to={getPublicFileUrl('upload/sample-bulk-addresses.xlsx')}
                        download
                        target="_blank"
                        className="button--primary"
                        style={{ textDecoration: 'none' }}
                    >
                        {t("Download Sample Address")}
                    </Link>
                    <br />
                    <div className="actions align--left">
                        <SingleFileUpload ref={fileUploaderRef} />
                    </div>
                    <i>{t("Supported formats : CSV ,XLSX")}</i>
                </div>

                <Modal
                    title={t("Selected Address")}
                    isVisible={addressPreview}
                    onClose={() => setAddressPreview(false)}
                >
                    <div className={isLoading ? 'loading' : ''}>
                        <p>{t("Please confirm if you intended to upload these addresses, as changes cannot be made afterwards")}</p>
                        <div className="actions">
                            <button
                                className="button--primary button--large"
                                onClick={handleFilesUpload}
                                disabled={isLoading}
                            >
                                {t("Yes")}
                            </button>
                            <button
                                className="button--cancel button--large"
                                onClick={() => setAddressPreview(false)}
                                disabled={isLoading}
                            >
                                {t("Cancel")}
                            </button>
                        </div>
                    </div>
                </Modal>

                <div className="actions align--right">
                    <button
                        className="button--primary"
                        onClick={() => setAddressPreview(true)}
                    >
                        {t("Confirm Delivery Address")}
                    </button>
                </div>
            </div>
        </div>
    )


}
BulkDeliveryAddresses.propTypes = {
    uploadedFile: PropTypes.object,
    quotationToken: PropTypes.string.isRequired,
    isConfirmed: PropTypes.bool

}

export default BulkDeliveryAddresses
